// components/team/UpdateRoleModal.tsx
import React, { useEffect, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from "../../utils/constants";

type Role = {
    id: string;
    tenantId: string;
    name: string;
    systemRole: boolean;
    permissions: string[];
    createdAt: string;
    updatedAt: string;
    version: number;
    archived: boolean;
    deletedAt: string | null;
};

type RolesResponse = {
    roles: Role[];
};

type UpdateRoleModalProps = {
    isOpen: boolean;
    onClose: () => void;
    memberIndex: number | null;
    currentRoleName: string;
    memberId: string;
    onRoleUpdated: (index: number, newRoleName: string) => void;
};

const FiXComponent = FiX as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;

const UpdateRoleModal: React.FC<UpdateRoleModalProps> = ({
    isOpen,
    onClose,
    memberIndex,
    currentRoleName,
    memberId,
    onRoleUpdated,
}) => {
    const [roles, setRoles] = useState<Role[]>([]);
    const [loadingRoles, setLoadingRoles] = useState(false);
    const [selectedRoleId, setSelectedRoleId] = useState<string>('');
    const [isUpdating, setIsUpdating] = useState(false);

    // Fetch roles from API
    const fetchRoles = async () => {
        try {
            setLoadingRoles(true);
            const { token, tenantId, userId } = getAuthContext();

            if (!token || !tenantId) {
                toast.error('Authentication required');
                return;
            }

            const response = await fetch(`${BASE_URL}/v1/team/roles`, {
                method: 'GET',
                headers: {
                    'X-Tenant-Id': tenantId,
                    'X-User-Id': userId || '',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch roles: ${response.status}`);
            }

            const data: RolesResponse = await response.json();
            // Filter out "Deleted role" and "Owner"
            setRoles(data.roles.filter(role => role.name !== 'Deleted role' && role.name !== 'Owner'));
        } catch (error: any) {
            // console.error('Failed to fetch roles:', error);
            toast.error('Failed to load roles');
        } finally {
            setLoadingRoles(false);
        }
    };

    // Find the role ID for the current role name
    useEffect(() => {
        if (isOpen && roles.length > 0 && currentRoleName && currentRoleName !== 'No Role') {
            const currentRole = roles.find(role => role.name === currentRoleName);
            if (currentRole) {
                setSelectedRoleId(currentRole.id);
            } else {
                // If current role not found (e.g., it was deleted), don't select anything
                setSelectedRoleId('');
            }
        } else if (isOpen && currentRoleName === 'No Role') {
            // If current role is "No Role", don't pre-select anything
            setSelectedRoleId('');
        }
    }, [isOpen, roles, currentRoleName]);

    // Fetch roles when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchRoles();
        } else {
            // Reset state when modal closes
            setSelectedRoleId('');
            setIsUpdating(false);
        }
    }, [isOpen]);

    // Check if the selected role is the same as the current role
    const isSameRole = () => {
        // If no role is selected, it's not the same (button should be enabled)
        if (!selectedRoleId) return false;

        // If current role is "No Role" or empty, it's never the same
        if (!currentRoleName || currentRoleName === 'No Role') return false;

        // Find the current role object
        const currentRole = roles.find(role => role.name === currentRoleName);

        // If current role not found in the list, it's not the same
        if (!currentRole) return false;

        // Compare the IDs
        return currentRole.id === selectedRoleId;
    };

    // Handle role update
    const handleUpdateRole = async () => {


        // Use memberId instead of memberIndex for the API call
        if (!memberId) {
            toast.error('Member ID not found');
            return;
        }

        if (!selectedRoleId) {
            toast.error('Please select a role');
            return;
        }

        try {
            setIsUpdating(true);
            const { token, tenantId, userId } = getAuthContext();

            if (!token || !tenantId) {
                toast.error('Authentication required');
                setIsUpdating(false);
                return;
            }


            const loadingToast = toast.loading('Updating role...');

            const response = await fetch(`${BASE_URL}/v1/team/members/${memberId}/role`, {
                method: 'PATCH',
                headers: {
                    'X-Tenant-Id': tenantId,
                    'X-User-Id': userId || '',
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    roleId: selectedRoleId,
                }),
            });

            toast.dismiss(loadingToast);

            if (!response.ok) {
                let errorMessage = `Failed to update role: ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorMessage;
                    // console.error('Error details:', errorData);
                    toast.error(errorMessage);
                } catch (e) {
                    // Ignore JSON parsing error
                }
                throw new Error(errorMessage);
            }

            // Get the new role name
            const selectedRole = roles.find(role => role.id === selectedRoleId);
            const newRoleName = selectedRole ? selectedRole.name : '';


            // Call the callback with the memberIndex if available
            if (memberIndex !== null) {
                onRoleUpdated(memberIndex, newRoleName);
            } else {
                console.warn('⚠️ memberIndex is null, cannot update parent state');
                toast.info('Role updated successfully!');
            }

            toast.success('Role updated successfully!');
            onClose();
        } catch (error: any) {
            // console.error('Update role error:', error);
            toast.error(error.message || 'Failed to update role');
        } finally {
            setIsUpdating(false);
        }
    };

    if (!isOpen) return null;

    // Check if update button should be disabled
    const isUpdateDisabled = !selectedRoleId || isSameRole() || loadingRoles || isUpdating;

    return (
        <div className='fixed inset-0 flex items-center justify-center bg-black/50 z-50'>
            <div className='bg-white w-[480px] rounded-lg shadow-lg relative'>
                {/* Header */}
                <div className='flex justify-between items-center px-6 py-4 border-b'>
                    <h2 className='text-lg font-semibold'>Update Role</h2>
                    <button
                        onClick={onClose}
                        className='text-gray-500 hover:text-gray-700'
                    >
                        <FiXComponent size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className='px-6 py-4 space-y-4'>
                    <div>
                        <label className='block text-sm font-medium text-gray-700'>
                            Select New Role<span className='text-red-500'>*</span>
                        </label>
                        <select
                            value={selectedRoleId}
                            onChange={(e) => setSelectedRoleId(e.target.value)}
                            className='mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#00A4BD] outline-none'
                            disabled={loadingRoles || isUpdating}
                        >
                            <option value=''>
                                {loadingRoles ? 'Loading roles...' : 'Select a role'}
                            </option>
                            {roles
                                .filter(role => role.name !== 'Deleted role' && role.name !== 'Owner')
                                .map((role) => (
                                    <option key={role.id} value={role.id}>
                                        {role.name} {role.name === currentRoleName ? '(Current)' : ''}
                                    </option>
                                ))}
                        </select>
                        {currentRoleName && (
                            <p className='text-xs text-gray-500 mt-1'>
                                Current role: <span className='font-medium'>{currentRoleName}</span>
                            </p>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className='flex justify-end gap-3 px-6 py-4 border-t'>
                    <button
                        type='button'
                        onClick={onClose}
                        className='px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100'
                        disabled={isUpdating}
                    >
                        Cancel
                    </button>
                    <button
                        type='button'
                        onClick={handleUpdateRole}
                        disabled={isUpdateDisabled}
                        className={`px-4 py-2 rounded-md text-white ${isUpdateDisabled
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-[#00A4BD] hover:bg-[#00899E]'
                            }`}
                    >
                        {isUpdating ? 'Updating...' : 'Update'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UpdateRoleModal;